import { FC, PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Link } from "@/components/_molecules/navigation-controls/link";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { useDocumentTitle } from "@/hooks/use-document-title";

import { PasswordResetForm } from "../components/password-reset-form";
import { isValidPasswordResetToken } from "../services/auth.service";

export function PasswordResetPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("auth");
  useDocumentTitle(t("passwordReset.title"));

  const token = searchParams.get("token");

  if (!token) {
    return <NotFoundView />;
  }

  return <IsValidTokenInnerPage token={token} />;
}

export interface IsValidTokenInnerPageProps extends PropsWithChildren {
  token: string;
}

const IsValidTokenInnerPage: FC<IsValidTokenInnerPageProps> = ({ token }) => {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation("auth");

  useEffect(() => {
    checkTokenAndGetMetaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function checkTokenAndGetMetaData() {
    try {
      const { email } = await isValidPasswordResetToken(token);
      setEmail(email);
    } catch (e) {
      // implicit error handling, since e-mail stays null
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <></>;
  }

  if (!email) {
    return (
      <NotFoundView text={t("passwordReset.linkNotFound")}>
        <p className="mb-4 text-center text-xs text-muted-foreground">
          {t("passwordReset.requestNewLinkText")}{" "}
          <Link to="/forgot-password" className="font-semibold text-muted-foreground">
            {t("passwordReset.requestNewLink")}
          </Link>{" "}
          {t("passwordReset.requestNewLinkSuffix")}
        </p>
        <p className="mb-4 text-center text-xs text-muted-foreground">
          {t("passwordReset.canLoginText")}{" "}
          <Link to="/login" className="font-semibold text-muted-foreground">
            {t("passwordReset.directLogin")}
          </Link>
        </p>
      </NotFoundView>
    );
  }

  return <InnerPage token={token} email={email} />;
};

export interface InnerPageProps extends PropsWithChildren {
  token: string;
  email: string;
}

const InnerPage: FC<InnerPageProps> = ({ token, email }) => {
  const { t } = useTranslation("auth");

  useEffect(() => {}, []);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
      <div className="z-10 w-full max-w-md overflow-hidden rounded-sm border shadow-xl">
        <div className="flex flex-col items-center justify-center space-y-3 border-b px-4 py-6 pt-8 text-center sm:px-16">
          <h3 className="text-xl font-semibold">{t("passwordReset.title")}</h3>
          <p className="text-sm text-muted-foreground">
            {email
              ? t("passwordReset.chooseNewPasswordForAccount", { email })
              : t("passwordReset.chooseNewPassword")}
          </p>
        </div>

        <PasswordResetForm token={token} />

        <p className="mb-8 text-center text-sm text-muted-foreground">
          {t("login.rememberCredentials")}{" "}
          <Link to="/register" className="font-semibold text-muted-foreground">
            {t("login.signIn")}
          </Link>{" "}
        </p>
      </div>
    </div>
  );
};
